import {
  Card,
  Title,
  Text,
  Stack,
  Button,
  Box,
  ThemeIcon,
  SimpleGrid,
  Group,
  useMantineTheme,
} from "@mantine/core";
import { IconArrowRight, IconUsers, IconClock } from "@tabler/icons-react";
import EventHeader from "./EventHeader";
import SponsorsSection from "./SponsorsSection";
import CustomTextDisplay from "./CustomTextDisplay";
import { useTranslation } from "react-i18next";
import { getTranslatedMonth } from "@congrs/utils";

function LandingPage({
  event,
  eventDates,
  registrationStatus,
  registrationSettings,
  approvedTickets,
  sponsors,
  onProceed,
}) {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  const {
    registrationsOpenDate,
    registrationsCloseDate,
    maxCapacity,
    displaySponsors,
    customText,
  } = registrationSettings || {};

  const isCapacityFull = maxCapacity && approvedTickets >= maxCapacity;

  const hasRegistrationDetails =
    registrationsOpenDate || registrationsCloseDate || maxCapacity;

  const formatDate = (date) =>
    `${getTranslatedMonth(date, t)} ${new Date(date).getDate()}, ${new Date(date).getFullYear()}`;

  const InfoCard = ({ icon, label, value }) => (
    <Box
      p="md"
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.6)",
        borderRadius: "12px",
        border: "1px solid rgba(0, 0, 0, 0.08)",
      }}
    >
      <Group gap="sm" wrap="nowrap">
        <ThemeIcon size="lg" radius="md" variant="light">
          {icon}
        </ThemeIcon>
        <Box style={{ flex: 1 }}>
          <Text size="xs" c="dimmed" fw={500} tt="uppercase">
            {label}
          </Text>
          <Text size="sm" fw={600} mt={2}>
            {value}
          </Text>
        </Box>
      </Group>
    </Box>
  );

  const StatusMessage = ({ message, subtitle }) => (
    <Box
      p="lg"
      style={{
        backgroundColor: theme.colors[theme.primaryColor][0],
        borderRadius: "12px",
        border: `1px solid ${theme.colors[theme.primaryColor][2]}`,
        textAlign: "center",
      }}
    >
      <Text size="md" fw={500} c={theme.colors[theme.primaryColor][7]}>
        {message}
      </Text>
      {subtitle && (
        <Text size="sm" c="dimmed" mt="xs">
          {subtitle}
        </Text>
      )}
    </Box>
  );

  const renderStatusMessage = () => {
    if (registrationStatus === "open" && !isCapacityFull) {
      return (
        <Button
          size="xl"
          rightSection={<IconArrowRight size={20} />}
          onClick={onProceed}
          fullWidth
          radius="md"
          style={{
            height: "60px",
            fontSize: "16px",
            fontWeight: 600,
          }}
        >
          {t("proceed_to_registration")}
        </Button>
      );
    }

    if (registrationStatus === "not_open_yet") {
      return (
        <StatusMessage
          message={`${t("registration_not_open_yet")} ${formatDate(registrationsOpenDate)}`}
        />
      );
    }

    if (registrationStatus === "closed") {
      return <StatusMessage message={t("registration_closed")} />;
    }

    if (isCapacityFull) {
      return <StatusMessage message={t("event_full")} />;
    }

    return null;
  };

  return (
    <>
      <EventHeader event={event} eventDates={eventDates} showDetails={true} />

      <Card shadow="lg" p={0} radius="md" withBorder mb="xl">
        {/* Custom Content */}
        {customText && <CustomTextDisplay content={customText} />}

        {/* Registration Info */}
        <Box p="xl">
          <Stack gap="md">
            {hasRegistrationDetails && (
              <Box>
                <Title order={2} size="h3" mb="md">
                  {t("registration_details")}
                </Title>

                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                  {registrationsOpenDate && (
                    <InfoCard
                      icon={<IconClock size={18} />}
                      label={t("opens")}
                      value={formatDate(registrationsOpenDate)}
                    />
                  )}

                  {registrationsCloseDate && (
                    <InfoCard
                      icon={<IconClock size={18} />}
                      label={t("closes")}
                      value={formatDate(registrationsCloseDate)}
                    />
                  )}

                  {maxCapacity && (
                    <InfoCard
                      icon={<IconUsers size={18} />}
                      label={t("capacity")}
                      value={`${approvedTickets} / ${maxCapacity}`}
                    />
                  )}
                </SimpleGrid>
              </Box>
            )}

            {renderStatusMessage()}
          </Stack>
        </Box>
      </Card>

      <SponsorsSection sponsors={sponsors} displaySponsors={displaySponsors} />
    </>
  );
}

export default LandingPage;
